import React, { useState, useRef, useEffect } from 'react'
import { X, Type, Image, Trash2, Save } from 'lucide-react'
import { fabric } from 'fabric'
import toast from 'react-hot-toast'

const SignaturePanel = ({ onAddSignature, onClose }) => {
  const [signatureType, setSignatureType] = useState('text')
  const [signatureText, setSignatureText] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const canvasRef = useRef(null)
  const fabricCanvasRef = useRef(null)

  useEffect(() => {
    // Inicializar canvas de Fabric.js
    if (canvasRef.current) {
      fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
        width: 400,
        height: 200,
        backgroundColor: '#ffffff'
      })
    }

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose()
      }
    }
  }, [])

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImageFile(file)
      const reader = new FileReader()
      reader.onload = (event) => {
        setImagePreview(event.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const addTextToCanvas = () => {
    if (!signatureText.trim()) {
      toast.error('Ingrese el texto de la firma')
      return
    }

    if (fabricCanvasRef.current) {
      const text = new fabric.Text(signatureText, {
        left: 50,
        top: 100,
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: 26,  // 26px ≈ 0.68 cm - mismo tamaño que en configuración (1.6rem)
        fill: '#1f2937',
        fontStyle: 'bold'
      })
      
      fabricCanvasRef.current.add(text)
      fabricCanvasRef.current.renderAll()
    }
  }

  const addImageToCanvas = () => {
    if (!imagePreview) {
      toast.error('Seleccione una imagen de firma')
      return
    }

    if (fabricCanvasRef.current) {
      fabric.Image.fromURL(imagePreview, (img) => {
        img.scaleToWidth(150)
        img.set({
          left: 50,
          top: 50
        })
        fabricCanvasRef.current.add(img)
        fabricCanvasRef.current.renderAll()
      })
    }
  }

  const clearCanvas = () => {
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.clear()
      fabricCanvasRef.current.backgroundColor = '#ffffff'
      fabricCanvasRef.current.renderAll()
    }
  }

  const saveSignature = () => {
    if (!fabricCanvasRef.current) return

    const objects = fabricCanvasRef.current.getObjects()
    if (objects.length === 0) {
      toast.error('No hay firma en el canvas')
      return
    }

    // Obtener coordenadas y dimensiones
    const bounds = fabricCanvasRef.current.getObjects()[0].getBoundingRect()
    
    // Convertir canvas a imagen
    const dataURL = fabricCanvasRef.current.toDataURL({
      format: 'png',
      quality: 1
    })

    const signature = {
      type: signatureType,
      text: signatureText,
      x: Math.round(bounds.left),
      y: Math.round(bounds.top),
      width: Math.round(bounds.width),
      height: Math.round(bounds.height),
      imageUrl: dataURL,
      imageFile: imageFile
    }

    onAddSignature(signature)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Agregar Firma</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Tipo de Firma */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tipo de Firma
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => setSignatureType('text')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${
                  signatureType === 'text'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Type className="h-4 w-4" />
                <span>Texto</span>
              </button>
              <button
                onClick={() => setSignatureType('imagen')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${
                  signatureType === 'imagen'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Image className="h-4 w-4" />
                <span>Imagen</span>
              </button>
            </div>
          </div>

          {/* Contenido según el tipo */}
          {signatureType === 'text' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Texto de la Firma
              </label>
              <input
                type="text"
                value={signatureText}
                onChange={(e) => setSignatureText(e.target.value)}
                placeholder="Ingrese su nombre o firma"
                className="input-field"
              />
              <button
                onClick={addTextToCanvas}
                className="mt-2 btn-secondary text-sm"
              >
                Agregar al Canvas
              </button>
            </div>
          )}

          {signatureType === 'imagen' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Imagen de Firma
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="input-field"
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-w-xs max-h-32 object-contain border border-gray-300 rounded"
                  />
                  <button
                    onClick={addImageToCanvas}
                    className="mt-2 btn-secondary text-sm"
                  >
                    Agregar al Canvas
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Canvas de Firma */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vista Previa de la Firma
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <canvas
                ref={canvasRef}
                className="border border-gray-200 rounded"
              />
            </div>
            <div className="flex space-x-2 mt-2">
              <button
                onClick={clearCanvas}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
              >
                <Trash2 className="h-4 w-4" />
                <span>Limpiar</span>
              </button>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Cancelar
            </button>
            <button
              onClick={saveSignature}
              className="btn-primary flex items-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>Agregar Firma</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignaturePanel

